// ============================================================================
// Trazas del chat de IA → Langfuse, VÍA BACKEND (issue CC-CONSOLE#879).
// ============================================================================
// Antes este módulo creaba un cliente de Langfuse en el navegador con
// las llaves pública y SECRETA de Langfuse con prefijo VITE_. Todo VITE_* se incrusta
// en el bundle público, así que la llave secreta del proyecto quedaba a la
// vista de cualquiera (y el SDK de navegador con sólo la llave pública no puede
// crear trazas ni generaciones, sólo puntuaciones).
//
// Ahora el tracer junta las observaciones del turno en memoria y `flush()` las
// manda en un solo POST /ai-chat/traces; el backend valida, toma el usuario del
// token y las reenvía con su propio cliente de Langfuse (LANGFUSE_* en
// functions). Si el backend no tiene llaves, responde traced:false y listo.
// La API pública del módulo no cambia (aiChatStore sigue igual).
// ============================================================================
import api from "../utils/api";

interface TraceOptions {
  name: string;
  userId?: string;
  sessionId?: string;
  metadata?: Record<string, unknown>;
  tags?: string[];
}

interface GenerationOptions {
  name: string;
  model: string;
  input: unknown;
  output?: unknown;
  usage?: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  metadata?: Record<string, unknown>;
  startTime?: Date;
  endTime?: Date;
}

interface SpanOptions {
  name: string;
  input?: unknown;
  output?: unknown;
  metadata?: Record<string, unknown>;
  startTime?: Date;
  endTime?: Date;
}

type Observation = {
  type: "generation" | "span" | "event";
  name: string;
  model?: string;
  input?: unknown;
  output?: unknown;
  metadata?: Record<string, unknown>;
  startTime?: string;
  endTime?: string;
};

const TRACES_ENDPOINT = "/ai-chat/traces";
/** Mismo tope que el backend (aiChatTraceService.MAX_OBSERVATIONS). */
const MAX_OBSERVATIONS = 50;

const iso = (d?: Date) => (d instanceof Date ? d.toISOString() : undefined);

/**
 * Tope por campo (input/output). La generación lleva el historial completo del
 * chat y los spans el resultado de la herramienta en JSON: con una flota
 * grande un solo turno pasaba los 100 kB del `express.json` del backend, que
 * respondía 413 antes de llegar a la ruta y la traza se perdía en silencio.
 */
const MAX_FIELD_CHARS = 8_000;
/**
 * Presupuesto del POST completo en BYTES UTF-8 (lo que cuenta body-parser),
 * holgado bajo los 100 kB del backend.
 */
const MAX_BODY_BYTES = 80_000;
const utf8Bytes = (v: unknown) => new TextEncoder().encode(JSON.stringify(v)).length;

/** Recorta un valor serializado a `max` caracteres, con marca. Lo chico pasa intacto. */
export function boundForTrace(value: unknown, max = MAX_FIELD_CHARS): unknown {
  if (value === undefined || value === null) return value;
  let serialized: string | undefined;
  try {
    serialized = typeof value === "string" ? value : JSON.stringify(value);
  } catch {
    return "[no serializable]";
  }
  if (serialized === undefined || serialized.length <= max) return value;
  return `${serialized.slice(0, max)}… [truncado ${serialized.length - max} caracteres]`;
}

/**
 * Deja las observaciones bajo MAX_BODY_BYTES: si aun con el tope por campo no entra,
 * vacía input/output empezando por las observaciones más viejas (se conservan
 * nombre, tipo y tiempos: la traza sigue diciendo qué pasó).
 */
function fitBudget(observations: Observation[]): Observation[] {
  const size = (o: Observation) => utf8Bytes(o);
  let total = observations.reduce((n, o) => n + size(o), 0);
  return observations.map((o) => {
    if (total <= MAX_BODY_BYTES || (o.input === undefined && o.output === undefined)) return o;
    const trimmed = { ...o, input: undefined, output: "[omitido: tope de tamaño de la traza]" };
    total -= size(o) - size(trimmed);
    return trimmed;
  });
}

/**
 * Siempre "habilitado": decidir si hay llaves es trabajo del backend, que es
 * el único que las tiene. Se conserva por compatibilidad con aiChatStore.
 */
export function isLangfuseEnabled(): boolean {
  return true;
}

export class LangfuseTracer {
  private readonly options: TraceOptions;
  private pending: Observation[] = [];

  constructor(options: TraceOptions) {
    this.options = options;
  }

  private push(observation: Observation): void {
    if (this.pending.length >= MAX_OBSERVATIONS) return;
    this.pending.push({
      ...observation,
      input: boundForTrace(observation.input),
      output: boundForTrace(observation.output),
    });
  }

  generation(options: GenerationOptions): void {
    this.push({
      type: "generation",
      name: options.name,
      model: options.model,
      input: options.input,
      output: options.output,
      metadata: options.usage ? { ...options.metadata, usage: options.usage } : options.metadata,
      startTime: iso(options.startTime),
      endTime: iso(options.endTime),
    });
  }

  span(options: SpanOptions): void {
    this.push({
      type: "span",
      name: options.name,
      input: options.input,
      output: options.output,
      metadata: options.metadata,
      startTime: iso(options.startTime),
      endTime: iso(options.endTime),
    });
  }

  event(name: string, metadata?: Record<string, unknown>): void {
    this.push({ type: "event", name, metadata, startTime: new Date().toISOString() });
  }

  /** Las puntuaciones no se mandan desde el chat; se ignora sin error. */
  score(_name: string, _value: number, _comment?: string): void {}

  /** Manda lo acumulado al backend. Nunca lanza: la traza no puede romper el chat. */
  async flush(): Promise<void> {
    if (!this.pending.length || !this.options.sessionId) return;
    const pending = this.pending;
    this.pending = [];
    try {
      // Dentro del try: un valor no serializable no puede romper el chat.
      const body = {
        name: this.options.name,
        // No `sessionId`: en el cuerpo, validateActiveSession lo toma como id
        // de la sesión de LOGIN (sin x-session-id daba 404).
        chatSessionId: this.options.sessionId,
        tags: this.options.tags,
        metadata: this.options.metadata,
        observations: fitBudget(pending),
      };
      // Si ni vaciando el contenido entra (metadata enorme), no se manda: el
      // backend respondería 413 igual.
      if (utf8Bytes(body) > MAX_BODY_BYTES + 10_000) return;
      await api.post(TRACES_ENDPOINT, body);
    } catch {
      // Sin traza no pasa nada; el chat sigue.
    }
  }
}

export function createChatTracer(options: {
  sessionId: string;
  userId?: string;
  tags?: string[];
}): LangfuseTracer {
  return new LangfuseTracer({
    name: "ai-chat",
    sessionId: options.sessionId,
    // userId lo pone el backend desde el token; no se manda desde aquí.
    tags: options.tags ?? ["fleet-assistant"],
    metadata: {
      platform: "v5",
      feature: "ai-chat",
    },
  });
}
